import _ from 'lodash';
import * as moment from 'moment';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { ImageService } from 'app/core/services/image.service';
import { takeUntil, filter, map, first } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CampaignService } from '../../services/campaign.service';
import { Validators, UntypedFormGroup, UntypedFormControl, AbstractControl } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { selectCampaignList, selectCampaignSelected } from '../../features/campaign.selectors';
import { DomSanitizer } from '@angular/platform-browser';

import * as DialogActions from 'app/dialog/features/dialog.actions';
import { CAMPAIGN_TYPE } from 'app/virtual-event/virtual-event.enums';
import { ToastService } from 'app/toast/services/toast.service';
import { ChooseImageModalService } from 'app/core/services/choose-image-modal.service';
import { loadCampaignList } from 'app/campaign/features/campaign.actions';
import { SharedService } from 'app/virtual-event/services/shared.service';

@Component({
  selector: 'app-campaign-general-info',
  templateUrl: './campaign-general-info.component.html',
  styleUrls: ['./campaign-general-info.component.scss']
})
export class CampaignGeneralInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  public campaigns$: any = this.store.pipe(
    select(selectCampaignList),
    map(list => {
      return _.filter(list, { status: 0 });
    })
  )

  public campaign$ = this.store.pipe(
    select(selectCampaignSelected)
  );

  @Input() panel: any = { expanded: false };
  @Output() afterCollapse = new EventEmitter();
  @Output() afterExpand = new EventEmitter();

  @Input() formGroupCampaign: any = null;
  public campaignTypeData: UntypedFormGroup = null;
  public additionalInfoSelect: UntypedFormControl = null;
  public images;
  public campaignDescPopover = 'Add a detailed description to describe this campaign. This is displayed anytime the donors are viewing this campaign.'

  @Input() settings: any = {
    qualifiedTaxDeductible: true,
    isDefault: true,
    dedicationEnabled: true,
    streetAddress: true,
    phoneNumber: true,
    additionalInfo: true,
    additionalInfoSelect: true,
    coCampaigns: true,
    addOnDonationCampaign: false,
    redirectUrl: true,
    donationButtonLabel: true,
    internalId: true,
    postDonation: false
  }

  public startDateOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  };

  public endDateOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  };

  public quillOptions = {
    showToolbarList: false,
    size: 'sm'
  }

  public addOnCampaigns: any = [];
  public coCampaigns: any = [];
  
  public takeUntilSubject: Subject<any> = new Subject();

  constructor(
    private store: Store,
    private imageService: ImageService,
    private campaignService: CampaignService,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private chooseImageModalService: ChooseImageModalService,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.campaignTypeData = this.formGroupCampaign.get('campaignTypeData');
    this.additionalInfoSelect = this.formGroupCampaign.get('additionalInfoSelect');
    let type = this.formGroupCampaign.get('type');

    this.campaignDescPopover = type.value == 6 ? 
          'Add a detailed description to describe this campaign. This is displayed anytime the donors are viewing this campaign. It is also added to the ticket confirmation emails.' :
          "Add a detailed description to describe this campaign. This is displayed anytime the donors are viewing this campaign."

              // Load campaign list only once if not present
    this.campaigns$.pipe(
      filter((list: any) => !list || list.length === 0),
      first(),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.store.dispatch({
        type: loadCampaignList.type,
        payload: this.sharedService.token
      });
    });

    this.campaigns$.pipe(
      filter((list: any) => list.length > 0),
      takeUntil(this.takeUntilSubject)
    ).subscribe((list) => {
      this.addOnCampaigns = list.filter(campaign => campaign.status === 0 && campaign.type === CAMPAIGN_TYPE.DONATE);
      this.coCampaigns = this.getCoCampaigns(list);
    });
    
    this.setImageSubscriptions('image');
    this.setImageSubscriptions('thumbnail');

    this.formGroupCampaign.get('campaignPageImageRequired').valueChanges.pipe(
      filter(value => !value),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.formGroupCampaign.get('campaignPageImage').setValue('');
    });

    this.formGroupCampaign.get('imageBelowFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      this.images = value;
      this.openCropperModal(value, 'image-below', null);
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'image-below'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      this.formGroupCampaign.get('campaignPageImage').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'image-below'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImageBelow();
      } else {
        this.formGroupCampaign.get('campaignPageImage').setValue(result.imageUrl);
      }

      this.cdr.markForCheck();
    });

    const endDate = this.formGroupCampaign.get('endDateUnix');
    const startDate = this.formGroupCampaign.get('startDateUnix')

    this.campaign$.pipe(
      filter(campaign => campaign !== null),
      takeUntil(this.takeUntilSubject)
    ).subscribe((campaign) => {
      endDate.setValidators([function(control: AbstractControl) {
        const endDateValue = control.value ? moment(control.value, 'MM/DD/YYYY').unix() : 0;
        const startDateValue = startDate.value ? moment(startDate.value, 'MM/DD/YYYY').unix() : 0;
        if(endDateValue === 0) {
          return null;
        }
        return startDateValue > endDateValue ? { incorrectDate: { value: endDateValue } } : null;
      }]);
      endDate.updateValueAndValidity();
      
      if(!_.isNull(startDate.value)) {
        this.endDateOptions = this.getEndDateOptions(startDate.value);
      }
    });

    startDate.valueChanges.pipe(
      filter(value => !_.isNull(value)),
      map(this.getEndDateOptions.bind(this)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(endDateOptions => {
      this.endDateOptions = endDateOptions;
      endDate.updateValueAndValidity();
    });

    this.campaignService.configGroup(this.formGroupCampaign, this.formGroupCampaign, 'streetAddressRequired', [
      { name: 'streetAddressReason', defaultValue: "", validators: [Validators.required] },
    ], this.takeUntilSubject);

    this.campaignService.configGroup(this.formGroupCampaign, this.formGroupCampaign, 'redirectUrlRequired', [
      { name: 'redirectUrl', defaultValue: "", validators: [Validators.required, (control: AbstractControl) => {
          if(control.value === "") {
              return null;
          }

          let url = true;
          try {
            const urlString = control.value.substr(0,4) === "www." ? `https://${control.value}`: control.value;
            new URL(urlString)
          } catch {
            url = false;
          }

          return url ? null : { url: true };
      }]
    }], this.takeUntilSubject);

    this.campaignService.configGroup(this.formGroupCampaign, this.formGroupCampaign, 'additionalInfoRequired', [
      { name: 'additionalInfoDesc', defaultValue: "", validators: [Validators.required] },
      { name: 'additionalInfoDetail', defaultValue: "", validators: [] }
    ], this.takeUntilSubject);

    this.campaignService.configGroup(this.formGroupCampaign, this.formGroupCampaign, 'additionalInfoSelectRequired', [
      { name: 'additionalInfoSelectDesc', defaultValue: "", validators: [Validators.required] },
      { name: 'additionalInfoSelect', defaultValue: [], validators: [Validators.required] }
    ], this.takeUntilSubject);

    this.campaignService.configGroup(this.formGroupCampaign, this.formGroupCampaign, 'coCampaignsRequired', [
      { name: 'coCampaigns', defaultValue: [], validators: [Validators.required] }
    ], this.takeUntilSubject);

    const addOnDonationCampaignRequired = this.campaignTypeData.get('addOnDonationCampaignRequired');
    if(addOnDonationCampaignRequired) {
      this.campaignService.configGroup(this.campaignTypeData, this.campaignTypeData, 'addOnDonationCampaignRequired', [
        { name: 'addOnDonationCampaignId', defaultValue: null, validators: [Validators.required] }
      ], this.takeUntilSubject);
    }

    this.campaignService.configGroup(this.formGroupCampaign, this.formGroupCampaign, 'donationButtonLabelRequired', [
      { name: 'donationButtonLabel', defaultValue: "", validators: [Validators.required, Validators.pattern("[0-9a-zA-Z-_\ ]+")] }
    ], this.takeUntilSubject);
  }

  ngAfterViewInit(): void {
    this.updateToolpips()
  }

  ngOnDestroy() {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
  }

  getStartDateOptions(date) {
    const disableSince = _.chain(date).split("/").map(element => {
      return parseInt(element);
    }).value();
    return {
      ...this.startDateOptions,
      disableSince: {
        year: disableSince[2],
        month: disableSince[0],
        day: disableSince[1]
      }
    }
  }

  getEndDateOptions(date) {
    const disableUntil = _.chain(date).split("/").map(element => {
      return parseInt(element);
    }).value();
    return {
      ...this.endDateOptions,
      disableUntil: {
        year: disableUntil[2],
        month: disableUntil[0],
        day: disableUntil[1]
      }
    }
  }

  setImageSubscriptions(type = 'image') {
    const ratio = type === 'image' ? 2/1 : 1/1;

    this.formGroupCampaign.get(`${type}File`).valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      if (value && value.length) {
        if(value == 'size error'){
          this.toastService.showDanger('The picture must be at least 200px by 200px.', {delay: 3000});
        }else{
          value.forEach(image => {
            this.openCropperModal(image, `campaign-${type}`, ratio)
          });
        }
      } else {
        this.openCropperModal(value, `campaign-${type}`, ratio)
      }
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === `campaign-${type}`),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      this.formGroupCampaign.get(`${type}DataUrl`).setValue(result);
    });
    
    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === `campaign-${type}`),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImageByType(`${type}`);
      } else {
        this.formGroupCampaign.get(`${type}Url`).setValue(result.imageUrl);
      }
      this.cdr.markForCheck();
    });

    this.updateToolpips()
  }
  
  openCropperModal(value, id, aspectRatio = 2/1) {
    const windowURL = window.URL || window.webkitURL;
    const url = windowURL.createObjectURL(value);
    this.imageService.cropper({
      url: this.domSanitizer.bypassSecurityTrustUrl(url),
      id: id,
      name: value.name,
      type: value.type,
      aspectRatio
    });
  }

  resetImageByType(type = 'image') {
    const ratio = type === 'image' ? '2x1' : '1x1';
    this.formGroupCampaign.get(`${type}File`).setValue(null);
    this.formGroupCampaign.get(`${type}DataUrl`).setValue(`/images/blank_${ratio}.png`);
    this.formGroupCampaign.get(`${type}Url`).setValue(`/images/blank_${ratio}.png`);
  }

  resetImageBelow(){
    this.formGroupCampaign.get('imageBelowFile').setValue(null);
    this.formGroupCampaign.get('campaignPageImage').setValue('');
  }

  onAfterCollapse() {
    this.afterCollapse.next(null);
  }

  onAfterExpand() {
    this.afterExpand.next(null);
  }

  video() {
    const action = DialogActions.openDialog({
      payload: {
        name: "video",
        options: {
          size: 'large',
          modalClass:'modal--paybee modal-lg',
          containerClass:'mdb-modal-backdrop-paybee'
        },
      }
    });
    this.store.dispatch(action);
  }

  openChooseImageModal(type = 0, placeOfCall = null) { // 0 for 2x1, 1 for 1x1
    const params = {
      start: 0,
      returnCount: 40,
      filterBy:[type]
    }

    this.campaignService.getOrganizationImages(params).pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe((imageList: any)=> {
      const list = imageList || [];
      const images = list.filter((image) => image.type === type)
      const modalRef = this.chooseImageModalService.open({ images, type });
    
      modalRef.onClose.pipe(
        takeUntil(this.takeUntilSubject)
      ).subscribe((imageUrl) =>{
        if(imageUrl) {
          if(placeOfCall === 'imageBelow') {
            this.formGroupCampaign.get('campaignPageImage').setValue(imageUrl);
          } else {
            const control = type === 0 ? 'image' : 'thumbnail';
            this.formGroupCampaign.get(`${control}DataUrl`).setValue(imageUrl);
            this.formGroupCampaign.get(`${control}Url`).setValue(imageUrl);
          }

          this.updateToolpips()
          this.toastService.show('success', 'Added Successfully!', {delay: 2000});
        }
        this.chooseImageModalService.modalRef = null;
      })
    })
  }

  updateToolpips(){
    window['$']('[data-toggle="tooltip"]').tooltip('dispose');
    window['$']('[data-toggle="tooltip"]').tooltip(
      {
        placement: 'left',
        trigger: 'click',
        html: true,
        template: '<div class="tooltip custom-tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>' 
    }
    )
  }

  generateThumbnail() {
    const imgUrl = this.formGroupCampaign.get('imageUrl').value || '';
    if(imgUrl === "" || imgUrl === "/images/blank_2x1.png") {
      this.toastService.show('danger', 'Please upload a campaign image first.', {delay: 2500});
      return;
    }

    const params = {
      imageURL: imgUrl,
      width: 200
    }

    this.campaignService.generateThumbnail(params).pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe((response: any)=> {
      const url = response.thumbnailURL || '/images/blank_1x1.png';

      this.formGroupCampaign.get('thumbnailDataUrl').setValue(url);
      this.formGroupCampaign.get('thumbnailUrl').setValue(url);
      this.toastService.show('success', 'Generated Successfully!', {delay: 2000});
    })
  }

  generateSummary(summarySize = 0) {
    const description = this.formGroupCampaign.get('desc').value || '';
    const summarySizeList = ['oneSentence', 'short', 'medium', 'long']

    if(description === "") {
      this.toastService.show('danger', 'Please add a campaign description first.', {delay: 2500});
      return;
    }

    const params = {
      text: description,
      summarySize: summarySizeList[summarySize]
    }

    this.campaignService.generateCSummary(params).pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe((response: any)=> {
      this.formGroupCampaign.get('summary').setValue(response.summary);
      this.toastService.show('success', 'Generated Successfully!', {delay: 2000});
    })
  }

  private getCoCampaigns(list: any) {
    if(this.formGroupCampaign === null) {
      return list;
    }

    const id = this.formGroupCampaign.get('id');
    return _.filter(list, campaign => {
      return id.value !== campaign.id;
    });
  }
}
